import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, LoaderCircle, Plus, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { Controller, type UseFormSetError, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";
import type { Equipo } from "@/lib/equipos";

// Espejo de la validación de servidor (factory/procedimiento_dto.go): el procedimiento
// plantilla más la selección de destino. La regla "un equipo o todos los loteables" se
// valida con un refine que marca el campo id_equipos.
export const asignacionMasivaSchema = z
  .object({
    codigo_procedimiento: z
      .string()
      .trim()
      .min(1, "El código es obligatorio")
      .max(60, "Máximo 60 caracteres"),
    version_procedimiento: z
      .string()
      .trim()
      .min(1, "La versión es obligatoria")
      .max(30, "Máximo 30 caracteres"),
    pasos: z
      .array(z.object({ text: z.string().trim().min(1, "El paso no puede estar vacío").max(500) }))
      .min(1, "Añade al menos un paso"),
    verificacion_energia_cero: z.string().trim().max(2000, "Máximo 2000 caracteres"),
    activo: z.boolean(),
    todos_loteables: z.boolean(),
    id_equipos: z.array(z.number().int().positive()),
  })
  .refine((v) => v.todos_loteables || v.id_equipos.length > 0, {
    message: "Selecciona al menos un equipo o marca todos los loteables",
    path: ["id_equipos"],
  });

export type AsignacionMasivaFormValues = z.infer<typeof asignacionMasivaSchema>;

const CAMPOS_SERVIDOR = [
  "codigo_procedimiento",
  "version_procedimiento",
  "pasos",
  "verificacion_energia_cero",
  "id_equipos",
] as const;

function aplicarError(
  err: unknown,
  setError: UseFormSetError<AsignacionMasivaFormValues>,
  setFormError: (msg: string) => void,
): void {
  if (err instanceof ApiError) {
    if (err.code === "VALIDACION" && err.fields) {
      let mapped = false;
      for (const campo of CAMPOS_SERVIDOR) {
        const msg = err.fields[campo];
        if (msg) {
          setError(campo, { message: msg });
          mapped = true;
        }
      }
      if (mapped) {
        return;
      }
    }
    setFormError(err.message || "No se pudo asignar el procedimiento.");
    return;
  }
  setFormError("No se pudo asignar el procedimiento. Comprueba tu conexión.");
}

/**
 * AsignacionMasivaForm clona un procedimiento hacia VARIOS equipos: define la plantilla
 * (código, versión, pasos, verificación) y el destino, bien marcando "todos los
 * loteables", bien seleccionando equipos concretos. El backend crea un registro por
 * equipo en una única transacción atómica (#31).
 */
export function AsignacionMasivaForm({
  equipos,
  pending,
  onSubmit,
  onCancel,
}: {
  equipos: Equipo[];
  pending: boolean;
  onSubmit: (values: AsignacionMasivaFormValues) => Promise<void>;
  onCancel: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<AsignacionMasivaFormValues>({
    resolver: zodResolver(asignacionMasivaSchema),
    defaultValues: {
      codigo_procedimiento: "",
      version_procedimiento: "1",
      pasos: [{ text: "" }],
      verificacion_energia_cero: "",
      activo: true,
      todos_loteables: false,
      id_equipos: [],
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: "pasos" });

  // Solo los equipos loteables son destino seleccionable (los que admiten LOTO).
  const loteables = equipos.filter((e) => e.loteable);
  const todosLoteables = watch("todos_loteables");

  const submit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      aplicarError(err, setError, setFormError);
    }
  });

  const pasosError = errors.pasos?.root?.message ?? errors.pasos?.message;

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      {formError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{formError}</span>
        </Alert>
      ) : null}

      <div className="flex gap-3">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="codigo_procedimiento_masiva">Código</Label>
          <Input
            id="codigo_procedimiento_masiva"
            autoComplete="off"
            aria-invalid={errors.codigo_procedimiento ? true : undefined}
            {...register("codigo_procedimiento")}
          />
          <FieldError>{errors.codigo_procedimiento?.message}</FieldError>
        </div>
        <div className="flex w-32 flex-col gap-1.5">
          <Label htmlFor="version_procedimiento_masiva">Versión</Label>
          <Input
            id="version_procedimiento_masiva"
            autoComplete="off"
            aria-invalid={errors.version_procedimiento ? true : undefined}
            {...register("version_procedimiento")}
          />
          <FieldError>{errors.version_procedimiento?.message}</FieldError>
        </div>
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 font-medium text-slate-800 text-sm">Pasos</legend>
        {fields.map((field, index) => (
          <div key={field.id} className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="w-6 shrink-0 text-right text-slate-500 text-sm tabular-nums">
                {index + 1}.
              </span>
              <Input
                aria-label={`Paso ${index + 1}`}
                autoComplete="off"
                aria-invalid={errors.pasos?.[index]?.text ? true : undefined}
                {...register(`pasos.${index}.text` as const)}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Eliminar paso ${index + 1}`}
                disabled={fields.length === 1}
                onClick={() => remove(index)}
              >
                <Trash2 className="h-5 w-5 text-red-600" aria-hidden="true" />
              </Button>
            </div>
            <FieldError className="pl-8">{errors.pasos?.[index]?.text?.message}</FieldError>
          </div>
        ))}
        <FieldError>{pasosError}</FieldError>
        <Button
          type="button"
          variant="outline"
          className="self-start"
          onClick={() => append({ text: "" })}
        >
          <Plus className="h-5 w-5" aria-hidden="true" />
          Añadir paso
        </Button>
      </fieldset>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="verificacion_energia_cero_masiva">
          Verificación de energía cero (opcional)
        </Label>
        <Textarea
          id="verificacion_energia_cero_masiva"
          aria-invalid={errors.verificacion_energia_cero ? true : undefined}
          {...register("verificacion_energia_cero")}
        />
        <FieldError>{errors.verificacion_energia_cero?.message}</FieldError>
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 font-medium text-slate-800 text-sm">Equipos destino</legend>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
            {...register("todos_loteables")}
          />
          <span className="text-base text-slate-800">Todos los equipos loteables</span>
        </label>

        <Controller
          control={control}
          name="id_equipos"
          render={({ field }) => (
            <div className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {loteables.length === 0 ? (
                <p className="px-1 py-2 text-slate-500 text-sm">No hay equipos loteables.</p>
              ) : (
                loteables.map((equipo) => {
                  const checked = field.value.includes(equipo.id);
                  return (
                    <label
                      key={equipo.id}
                      className="flex items-center gap-3 rounded px-1 py-1 hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50"
                        disabled={todosLoteables}
                        checked={checked}
                        onChange={(e) => {
                          field.onChange(
                            e.target.checked
                              ? [...field.value, equipo.id]
                              : field.value.filter((id) => id !== equipo.id),
                          );
                        }}
                      />
                      <span className="text-base text-slate-800">
                        {equipo.codigo_equipo} · {equipo.nombre_equipo}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          )}
        />
        <FieldError>{errors.id_equipos?.message}</FieldError>
      </fieldset>

      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
          {...register("activo")}
        />
        <span className="text-base text-slate-800">Activo</span>
      </label>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={pending}>
          {pending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Users className="h-5 w-5" aria-hidden="true" />
          )}
          Asignar
        </Button>
      </div>
    </form>
  );
}
